import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { AppConfig, SessionInfo } from "../lib/types";

const SAMPLE_ORU = [
  "MSH|^~\\&|LAB|CLINIC|PEDOB|PULSELOGIC|20260920101500||ORU^R01|MSG0001|P|2.5.1",
  "PID|1||MRN5550^^^CLINIC||Test^Jordan||20120305|M",
  "OBR|1||A1|LIPID^Lipid panel|||20260920",
  "OBX|1|NM|8302-2^Body height^LN||158|cm|||||F|||20260920",
  "OBX|2|NM|29463-7^Body weight^LN||79|kg|||||F|||20260920",
  "OBX|3|NM|8480-6^Systolic BP^LN||128|mm[Hg]|||||F|||20260920",
  "OBX|4|NM|8462-4^Diastolic BP^LN||78|mm[Hg]|||||F|||20260920",
  "OBX|5|NM|13457-7^LDL cholesterol^LN||136|mg/dL|||||F|||20260920",
  "OBX|6|NM|4548-4^Hemoglobin A1c^LN||5.9|%|||||F|||20260920",
].join("\n");

export default function Integrations({ config, session }: { config: AppConfig | null; session: SessionInfo }) {
  const [hl7, setHl7] = useState(SAMPLE_ORU);
  const [ack, setAck] = useState<{ ack: string; patientId: string | null } | null>(null);
  const [discovery, setDiscovery] = useState<unknown>(null);
  const [fhirPts, setFhirPts] = useState<{ id: string; name: string | null; birth_date: string; gender: string }[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const origin = window.location.origin;

  useEffect(() => {
    api.cdsDiscovery().then(setDiscovery).catch(() => setDiscovery(null));
    if (config?.fhir_base) api.fhirPatients().then(setFhirPts).catch((e: Error) => setErr(e.message));
  }, [config?.fhir_base]);

  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(420px, 100%), 1fr))" }}>
      <section className="card">
        <h2>SMART on FHIR app</h2>
        <dl className="kv small">
          <dt>Launch URL</dt>
          <dd><code>{origin}/launch.html</code></dd>
          <dt>Redirect URL</dt>
          <dd><code>{origin}/index.html</code></dd>
          <dt>Scopes</dt>
          <dd>launch, openid, fhirUser, patient/*.read (Patient, Observation, Condition, MedicationRequest, FamilyMemberHistory), write for Observation, ServiceRequest, CarePlan, DocumentReference, Provenance</dd>
        </dl>
        <p className="small muted">
          Register these with the facility EHR (Epic, Oracle Health/Cerner, InterSystems TrakCare) or any SMART-enabled
          EHR. Regional exchanges: Malaffi (Abu Dhabi, DoH), NABIDH (Dubai, DHA), Riayati (MOHAP) - onboarding via each
          authority's integration process.
          Test now against the public sandbox:{" "}
          <a href={`https://launch.smarthealthit.org/?launch_url=${encodeURIComponent(origin + "/launch.html")}&fhir_ver=4`}
            target="_blank" rel="noreferrer">launch.smarthealthit.org</a>.
        </p>
      </section>

      <section className="card">
        <h2>CDS Hooks service</h2>
        <dl className="kv small">
          <dt>Discovery</dt>
          <dd><code>{origin}/cds-services</code></dd>
          <dt>Hook</dt>
          <dd>patient-view → <code>/cds-services/pedob-patient-view</code></dd>
          <dt>Auth</dt>
          <dd>EHR-signed JWT (ES384/RS384) verified against each trusted issuer's JWKS (<code>PEDOB_CDS_ISSUERS</code>)</dd>
        </dl>
        <p className="small muted">Test with the CDS Hooks sandbox (sandbox.cds-hooks.org) by adding the discovery URL.</p>
        <details>
          <summary className="small">Discovery document</summary>
          <pre className="code">{discovery ? JSON.stringify(discovery, null, 2) : "Unavailable"}</pre>
        </details>
      </section>

      <section className="card">
        <h2>HL7 v2 inbound</h2>
        <p className="small muted">
          POST to <code>{origin}/api/hl7/inbound</code>
          {config?.mllp_port ? <> or MLLP on port <code>{config.mllp_port}</code></> : " (MLLP listener off: set PEDOB_MLLP_PORT)"}.
          ADT^A01/A04/A08 and ORU^R01 with LOINC-coded OBX.
        </p>
        <p className="small muted">
          Interface engines authenticate with the <code>X-API-Key</code> header (<code>PEDOB_HL7_API_KEY</code>).
          {session.role !== "admin" && " The tester below is available to admins."}
        </p>
        <textarea rows={9} value={hl7} onChange={(e) => setHl7(e.target.value)} style={{ fontFamily: "ui-monospace, monospace", fontSize: 12.5 }} />
        <div className="row" style={{ marginTop: 8 }}>
          <button className="btn primary" disabled={session.role !== "admin"} onClick={() => api.hl7Inbound(hl7.replace(/\n/g, "\r")).then(setAck).catch((e: Error) => setErr(e.message))}>
            Send message
          </button>
          {ack?.patientId && <a href={`#/patient/${encodeURIComponent(ack.patientId)}`}>Open {ack.patientId} →</a>}
        </div>
        {ack && <pre className="code">{ack.ack.replace(/\r/g, "\n")}</pre>}
      </section>

      <section className="card">
        <h2>FHIR server</h2>
        {config?.fhir_base ? (
          <>
            <p className="small">Connected: <code>{config.fhir_base}</code></p>
            {fhirPts === null ? (
              <p className="muted small">Loading patients…</p>
            ) : (
              <div className="table-scroll">
              <table className="list small">
                <tbody>
                  {fhirPts.map((p) => (
                    <tr key={p.id} className="clickable" onClick={() =>
                      api.fhirImport(p.id).then(() => (window.location.hash = `#/patient/${encodeURIComponent(p.id)}`)).catch((e: Error) => setErr(e.message))}>
                      <td>{p.name ?? p.id}</td><td>{p.birth_date}</td><td>{p.gender}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </>
        ) : (
          <p className="small muted">
            Not configured. Set <code>PEDOB_FHIR_BASE</code> (e.g. the bundled HAPI FHIR server,{" "}
            <code>http://localhost:8080/fhir</code>) to pull patients server-side and write back with a backend token.
          </p>
        )}
      </section>
      {err && <p className="error">{err}</p>}
    </div>
  );
}
